import api from '@/lib/api';

export interface Agent {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  role: 'agent' | 'admin' | 'warehouse';
  status: 'active' | 'inactive' | 'suspended';
  lastLogin: string | null;
  createdAt: string;
  stats?: {
    activeTickets: number;
    totalTickets: number;
    solvedTickets: number;
  };
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  role: 'admin' | 'agent' | 'customer' | 'warehouse';
  status: 'active' | 'inactive' | 'suspended';
  emailVerified: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
  organizationIds?: string[];
}

export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: 'admin' | 'agent' | 'customer' | 'warehouse';
  organizationIds?: string[];
}

export interface UpdateUserData {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'agent' | 'customer' | 'warehouse';
  status?: 'active' | 'inactive' | 'suspended';
  organizationIds?: string[];
}

export interface AgentsResponse {
  success: boolean;
  data: {
    agents: Agent[];
  };
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface CreateUserResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
    tokenType: string;
  };
}

class UsersService {
  async getAgents(includeStats = false, status = 'all'): Promise<AgentsResponse> {
    const params = new URLSearchParams();
    params.set('includeStats', includeStats.toString());
    params.set('status', status);
    
    const response = await api.get<AgentsResponse>(`/users/agents?${params.toString()}`);
    return response.data;
  }

  async getAllUsers(page = 1, limit = 20, role?: string, status?: string): Promise<UsersResponse> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    
    const response = await api.get<UsersResponse>(`/users?${params.toString()}`);
    return response.data;
  }

  async createUser(userData: CreateUserData): Promise<CreateUserResponse> {
    try {
      const response = await api.post<CreateUserResponse>('/auth/register', {
        ...userData,
        role: userData.role || 'agent' // Default role is agent for admin-created users
      });
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async updateUser(userId: string, userData: UpdateUserData): Promise<{ success: boolean; message: string; data: { user: User } }> {
    try {
      const response = await api.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async updateUserStatus(userId: string, status: 'active' | 'inactive' | 'suspended'): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/users/${userId}`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }
}

export default new UsersService();